import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Building2, ChevronDown, LogOut, Menu, Phone, ShoppingBag, UserRound, X } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { localized } from "@/theme/tenantTheme";
import { languageOptions, roleLabels } from "@/i18n/dictionaries";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/NotificationBell";
import { InstallAppButton } from "@/components/InstallAppButton";
import { CityWordmark } from "@/components/CityBrand";
import { AsLogo } from "@/components/AsLogo";
import { EmergencyDialog } from "@/components/EmergencyDialog";
import { trackInteraction } from "@/storefront/trackInteraction";
import { identityLabel } from "@/lib/format";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ConfirmDialog from "@/components/ConfirmDialog";
import { hereOf, loginPath } from "@/lib/loginPath";
import { resolveImage } from "@/lib/media";
import { useRoleSwitchConfirm } from "@/components/RoleSwitchConfirm";

export function Header({ consoleMode = false }) {
  const {
    t,
    lang,
    changeLanguage,
    tenant,
    tenants,
    mode,
    switchTenant,
    auth,
    signOut,
    cart,
    businessContext,
    storefrontBranding,
  } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [sosOpen, setSosOpen] = useState(false);
  const cityName = localized(lang, tenant, "name");
  const [signOutConfirm, setSignOutConfirm] = useState(false);
  // U1: whether nav links are hidden past the right edge (drives the scroll-cue fade).
  const navRef = useRef(null);
  const [navOverflows, setNavOverflows] = useState(false);
  useEffect(() => {
    const el = navRef.current;
    if (!el) return undefined;
    const measure = () => setNavOverflows(el.scrollWidth - el.clientWidth - el.scrollLeft > 4);
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure);
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  });
  // RF-18: "priority+" nav. At 1280-1920 the header row is capped at max-w-7xl, so 8 links plus the
  // action cluster never fit and links were scrolled under the cluster. Links that do not fit move
  // into a "More" menu instead. Link widths are measured once per label set (all links render
  // first), then the split is recomputed from the cache on every resize.
  const navBoxRef = useRef(null);
  const linkWidths = useRef({ key: "", widths: [] });
  const [visibleCount, setVisibleCount] = useState(Infinity);
  // R3-8: flips when the nav goes back to "all links rendered" (a language switch resets it), which
  // is the only moment link widths can be re-measured; without it the effect never re-ran after the
  // reset, the nav stayed at Infinity and the last links slid under the emergency button.
  const allShown = !Number.isFinite(visibleCount);
  const { requestSwitch, dialog: roleSwitchDialog } = useRoleSwitchConfirm();
  const goSignIn = () => navigate(loginPath(hereOf(location), tenant?.slug));
  // Custom-domain path (available at first paint) wins over the in-app path — see PR5's wiring
  // decision. Neither is populated for a free business, so `wl` is simply falsy there.
  const wl = businessContext?.logo_url ? businessContext : storefrontBranding;
  // White-label chrome must key off business eligibility (`wl`), not URL shape (L2-002): a
  // premium branded business is white-label on the plain `/<city>/<slug>` URL too, since that is
  // the address the storefront itself advertises — not just on `/b/:slug` or a custom domain.
  const minimal = mode === "business_site" || location.pathname.startsWith("/b/") || Boolean(wl);

  const languageToggleConsole = (
    <div className="flex items-center rounded-full border border-border p-0.5" data-testid="language-toggle">
      {languageOptions.map((o) => (
        <button
          key={o.code}
          onClick={() => changeLanguage(o.code)}
          data-testid={`language-option-${o.code}`}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
            lang === o.code ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  // R3-11: the console body spoke of "manage orders", which is an owner's job — a city-desk or
  // platform operator got the wrong copy. Pick the body by which console this is.
  const onDesk = /^\/(city-desk|platform)(\/|$)/.test(location.pathname || "");
  const signOutBodyKey = !consoleMode
    ? "account.signOutBody"
    : onDesk
      ? "account.signOutBodyDesk"
      : "account.signOutBodyStaff";

  // One sign-out confirm for every header variant (it used to exist only in the full header, so
  // the console header — the one an owner or operator actually works in — had no sign-out).
  const signOutDialog = (
    <ConfirmDialog
      open={signOutConfirm}
      onOpenChange={setSignOutConfirm}
      title={t("account.signOutTitle")}
      body={t(signOutBodyKey)}
      confirmLabel={t("common.signOut")}
      onConfirm={() => {
        setSignOutConfirm(false);
        signOut();
        navigate("/");
      }}
      testIds={{
        dialog: "sign-out-dialog",
        cancel: "sign-out-cancel-btn",
        ok: "sign-out-confirm-btn",
      }}
    />
  );

  // The cart button/badge, shared by the full header and the storefront (minimal) variants — the
  // minimal ones used to render none, so a shopper on a shop page never saw their cart (UAT L8).
  const cartButton =
    cart && cart.items?.length > 0 ? (
      <Button
        variant="outline"
        size="sm"
        className="h-11 min-w-11 gap-1 rounded-full"
        data-testid="header-cart-btn"
        aria-label={t("nav.cart")}
        onClick={() => navigate("/cart")}
      >
        <ShoppingBag size={14} />
        <span key={cart.items.length} data-testid="header-cart-count" className="inline-block animate-bump">
          {cart.items.length}
        </span>
      </Button>
    ) : null;

  // Business owner / operator / platform-admin consoles: same platform top bar as the citizen
  // site (city mark + lockup on the left, account/language on the right), never the business's
  // own white-label chrome or the citizen nav links. h-14 (56px), one row, so it stacks cleanly
  // above each console's own sticky sub-bars (owner mobile top bar, desk sidebar) without eating
  // too much of the viewport.
  // RF-18 priority-plus nav: these (and the measurement hook) MUST run on every render, so they
  // live above the `consoleMode` early return — a hook called after a conditional return violates
  // react-hooks/rules-of-hooks. `shownLinks`/`moreLinks` (plain consts) stay in the citizen render.
  const links = [
    { to: "/", label: t("nav.home"), testId: "nav-home" },
    { to: "/directory", label: t("nav.directory"), testId: "nav-directory" },
    { to: "/workers", label: t("nav.workers"), testId: "nav-workers" },
    { to: "/services", label: t("services.navLabel"), testId: "nav-services" },
    { to: "/offers", label: t("nav.offers"), testId: "nav-offers" },
    { to: "/events", label: t("nav.events"), testId: "nav-events" },
    { to: "/jobs", label: t("nav.jobs"), testId: "nav-jobs" },
    { to: "/community", label: t("nav.community"), testId: "nav-community" },
  ];
  const linksKey = links.map((l) => l.label).join("|");
  useLayoutEffect(() => {
    const box = navBoxRef.current;
    const nav = navRef.current;
    if (!box || !nav) return undefined;
    const fit = () => {
      const cache = linkWidths.current;
      if (box.clientWidth <= 8 || cache.key !== linksKey || cache.widths.some((w) => !w)) return setVisibleCount(Infinity);
      const GAP = 4; // gap-1
      const MORE = 88; // the "More" trigger incl. its gap
      // R3-8: a little slack so a link never ends flush against (or under) the action cluster.
      const avail = box.clientWidth - 8;
      const total = cache.widths.reduce((sum, w) => sum + w + GAP, 0);
      if (total <= avail) return setVisibleCount(Infinity);
      let used = MORE;
      let count = 0;
      for (const w of cache.widths) {
        if (used + w + GAP > avail) break;
        used += w + GAP;
        count += 1;
      }
      return setVisibleCount(count);
    };
    // (Re)measure while every link is rendered: first paint, or after a language change.
    if (linkWidths.current.key !== linksKey) {
      if (nav.children.length === links.length) {
        linkWidths.current = { key: linksKey, widths: Array.from(nav.children).map((c) => c.offsetWidth) };
      } else {
        setVisibleCount(Infinity); // not all rendered yet - the next pass measures
        return undefined;
      }
    }
    fit();
    window.addEventListener("resize", fit);
    // R3-8: the box is `flex-1`, so its width is whatever the action cluster leaves — and the
    // cluster changes WITHOUT a window resize (the Install-app button appears after
    // `beforeinstallprompt`, sign-in <-> account, a cart badge). Observing the box re-fits on
    // every such change, so "Offers" is no longer cut when Install shows up late.
    const ro = typeof ResizeObserver === "function" ? new ResizeObserver(fit) : null;
    ro?.observe(box);
    return () => {
      window.removeEventListener("resize", fit);
      ro?.disconnect();
    };
    // Re-measure on mount and when the links change (a language switch alters `linksKey`); the
    // resize listener covers width changes between renders. An empty/absent dep list re-ran this
    // every render (the RF-18 exhaustive-deps lint).
  }, [linksKey, links.length, allShown]);

  if (consoleMode) {
    return (
      <header
        className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur-xl"
        data-testid="console-header"
      >
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 sm:px-8">
          <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-center gap-2">
            <AsLogo size={30} className="text-[var(--as-primary)]" />
            <CityWordmark name={cityName} nameHi={tenant?.name_hi} className="text-base" />
          </Link>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {languageToggleConsole}
            {auth ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 min-w-10 gap-1 rounded-full"
                data-testid="account-menu-btn"
                onClick={() => navigate("/account")}
              >
                <UserRound size={16} />
                <span className="hidden sm:inline">{identityLabel(auth.identity)}</span>
              </Button>
            ) : (
              <Button size="sm" className="h-10 rounded-full" data-testid="sign-in-btn" onClick={goSignIn}>
                {t("common.signIn")}
              </Button>
            )}
            {auth && (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 min-w-10 gap-1 rounded-full"
                data-testid="console-sign-out-btn"
                aria-label={t("common.signOut")}
                onClick={() => setSignOutConfirm(true)}
              >
                <LogOut size={16} />
                <span className="hidden md:inline">{t("common.signOut")}</span>
              </Button>
            )}
          </div>
        </div>
        {signOutDialog}
      </header>
    );
  }

  const sosButton = (
    <Button
      variant="outline"
      size="icon"
      className="h-11 w-11 rounded-full border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/20"
      data-testid="sos-btn"
      aria-label={t("emergency.title")}
      onClick={() => setSosOpen(true)}
    >
      <Phone size={16} />
    </Button>
  );

  const languageToggle = (
    <div className="flex items-center rounded-full border border-border p-0.5" data-testid="language-toggle">
      {languageOptions.map((o) => (
        <button
          key={o.code}
          onClick={() => changeLanguage(o.code)}
          data-testid={`language-option-${o.code}`}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
            lang === o.code ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  const shownLinks = links.slice(0, Number.isFinite(visibleCount) ? visibleCount : links.length);
  const moreLinks = links.slice(shownLinks.length);

  // W-11 (take 2): a role-home entry used to live here too, pushed onto the end of `links`. Local
  // verification (QA/staging-uat-2026-10/VERIFICATION.md) found the FIRST W-11 fix (`pr-3`, just
  // below) never actually worked for any dual-role account — these 7 base links already leave
  // zero spare width at 1280px for a guest (`scrollWidth === clientWidth` exactly), so an 8th link
  // always overflowed the scrollable nav with no visual cue, clipping the next-to-last link
  // mid-glyph and leaving the actual last one entirely unreachable. Rather than patch the symptom
  // again, the role-home link moved out of this row: it's reachable from the account dropdown's
  // own "Account roles" list below, which already exists for role-SWITCHING and already contains
  // every role (including the sole one a single-role owner/worker/operator holds) —
  // `role_profiles` lists every tenant-scoped seat; only the citizen role is implicit/"national"
  // and absent from it, so there was never a case this list wouldn't cover.

  if (minimal) {
    // White-label: the business's own brand, no platform chrome (mark, SOS, sign-in). The
    // language toggle stays — it's the one platform control README keeps in white-label.
    if (wl) {
      return (
        <header
          className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl"
          data-testid="minimal-header"
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-5 sm:px-8">
            <div className="flex shrink-0 items-center gap-2" data-testid="whitelabel-brand">
              <img src={resolveImage(wl.logo_url)} alt={wl.name} className="h-9 w-9 rounded-full object-cover" />
              <div className="min-w-0 leading-tight">
                <p className="truncate font-heading text-sm font-semibold">{wl.name}</p>
                {localized(lang, wl, "brand_subtitle") && (
                  <p className="truncate text-xs text-muted-foreground">{localized(lang, wl, "brand_subtitle")}</p>
                )}
              </div>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              {wl.phone && (
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="gap-1 rounded-full"
                  data-testid="whitelabel-call-btn"
                  onClick={() => trackInteraction(wl.id, "call")}
                >
                  <a href={`tel:${wl.phone}`}>
                    <Phone size={14} />
                    <span className="hidden sm:inline">{t("common.call")}</span>
                  </a>
                </Button>
              )}
              {cartButton}
              {languageToggle}
            </div>
          </div>
        </header>
      );
    }
    return (
      <header
        className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl"
        data-testid="minimal-header"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-5 sm:px-8">
          {/* The brand never shrinks or distorts — everything else yields first. */}
          <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-center gap-2">
            <AsLogo size={36} className="text-[var(--as-primary)]" />
            <CityWordmark name={cityName} nameHi={tenant?.name_hi} className="text-lg" />
          </Link>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {sosButton}
            {cartButton}
            {languageToggle}
            {auth ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 min-w-11 gap-1 rounded-full"
                data-testid="account-menu-btn"
                onClick={() => navigate("/account")}
              >
                <UserRound size={16} />
                <span className="hidden sm:inline">{identityLabel(auth.identity)}</span>
              </Button>
            ) : (
              <Button size="sm" className="h-11 rounded-full" data-testid="sign-in-btn" onClick={goSignIn}>
                {t("common.signIn")}
              </Button>
            )}
          </div>
        </div>
        <EmergencyDialog open={sosOpen} onOpenChange={setSosOpen} />
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-5 sm:px-8">
        {/* The brand never shrinks or distorts — the nav scrolls instead when space runs out. */}
        <Link to="/" data-testid="brand-logo" className="flex shrink-0 items-center gap-2">
          <AsLogo size={36} className="text-[var(--as-primary)]" />
          <CityWordmark name={cityName} nameHi={tenant?.name_hi} className="text-lg" />
        </Link>

        {/* Removing the role-home link (see comment above `links`) fixes the common case, but this
            row can still outgrow its space on its own — a long display name or city name in the
            cluster to its right shrinks the room `flex-1` leaves it, at any viewport width, for
            any signed-in account. `overflow-x-auto` already makes every link genuinely reachable
            by scroll even then; this wrapper just adds the one thing that was still missing — a
            visible cue that there's more, instead of the row quietly ending after the last link
            that happened to fit. `pr-3` on the nav itself still gives a fully-visible last link
            real breathing room before this fade (or the cluster, when nothing overflows). */}
        <div ref={navBoxRef} className="relative ml-6 hidden min-w-0 flex-1 items-center gap-1 md:flex">
          <nav
            ref={navRef}
            className="flex min-w-0 items-center gap-1 overflow-x-auto whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {shownLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `rounded-full px-2.5 py-2 text-sm font-medium transition-colors duration-200 lg:px-3 xl:px-4 ${
                    isActive ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
          {moreLinks.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-11 shrink-0 gap-1 rounded-full" data-testid="nav-more-btn">
                  {t("nav.more")} <ChevronDown size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-48">
                {moreLinks.map((l) => (
                  <DropdownMenuItem key={l.to} data-testid={`more-${l.testId}`} onClick={() => navigate(l.to)}>
                    {l.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {/* U1: the fade is only a cue that there is more to scroll to - drawn while links are
              actually hidden past the right edge, never over the last link at rest. */}
          {navOverflows && (
            <div
              aria-hidden="true"
              data-testid="nav-scroll-fade"
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
            />
          )}
        </div>

        {/* L1-001/L4-005: at <sm (390px) this cluster's full width overflowed the viewport and
            caused site-wide horizontal scroll on every citizen page and both desk shells. Only
            SOS, cart and sign-in/account stay inline at every width (nothing else surfaces
            them); install-app, notifications and the language toggle collapse into the
            hamburger drawer below sm instead of being dropped. */}
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {sosButton}
          <div className="hidden sm:block">
            <InstallAppButton />
          </div>
          <div className="hidden sm:block">
            <NotificationBell />
          </div>
          {cartButton}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="hidden gap-1 rounded-full sm:flex" data-testid="city-switcher-btn">
                <Building2 size={14} />
                <span className="max-w-[7rem] truncate">{localized(lang, tenant, "name")}</span>
                <ChevronDown size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>{t("common.changeCity")}</DropdownMenuLabel>
              {tenants.map((c) => (
                <DropdownMenuItem
                  key={c.slug}
                  data-testid={`city-option-${c.slug}`}
                  onClick={() => switchTenant(c.slug)}
                >
                  {lang === "hi" ? c.name_hi || c.name : c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="hidden items-center rounded-full border border-border p-0.5 sm:flex" data-testid="language-toggle">
            {languageOptions.map((o) => (
              <button
                key={o.code}
                onClick={() => changeLanguage(o.code)}
                data-testid={`language-option-${o.code}`}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                  lang === o.code ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>

          {auth ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-11 min-w-11 gap-1 rounded-full" data-testid="account-menu-btn">
                  <UserRound size={16} />
                  <span className="hidden sm:inline">{identityLabel(auth.identity)}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel>{t("account.roles")}</DropdownMenuLabel>
                {auth.role_profiles?.map((rp) => (
                  <DropdownMenuItem
                    key={rp.id}
                    data-testid={`role-switch-${rp.role_type}`}
                    onClick={() => requestSwitch(rp)}
                  >
                    <span className="flex-1">{roleLabels[lang][rp.role_type]}</span>
                    {rp.id === auth.active_role_profile_id && (
                      <span className="text-xs text-[hsl(var(--primary))]">{t("account.activeRole")}</span>
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-activity" onClick={() => navigate("/activity")}>
                  {t("activity.title")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-favourites" onClick={() => navigate("/favourites")}>
                  {t("nav.favourites")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-account" onClick={() => navigate("/account")}>
                  {t("nav.account")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="sign-out-btn" onClick={() => setSignOutConfirm(true)}>
                  <LogOut size={14} className="mr-2" /> {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button size="sm" className="h-11 rounded-full" data-testid="sign-in-btn" onClick={goSignIn}>
              {t("common.signIn")}
            </Button>
          )}
          {signOutDialog}
          {roleSwitchDialog}

          <button
            className="grid min-h-11 min-w-11 place-items-center md:hidden"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("common.menu")}
            aria-expanded={open}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border bg-background px-5 py-3 md:hidden" data-testid="mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="block py-2 text-sm font-medium text-muted-foreground"
              data-testid={`mobile-${l.testId}`}
            >
              {l.label}
            </NavLink>
          ))}
          <div className="flex flex-wrap gap-2 pt-2">
            {tenants.map((c) => (
              <button
                key={c.slug}
                onClick={() => {
                  switchTenant(c.slug);
                  setOpen(false);
                }}
                data-testid={`mobile-city-${c.slug}`}
                className="rounded-full border border-border px-3 py-1 text-xs"
              >
                {lang === "hi" ? c.name_hi || c.name : c.name}
              </button>
            ))}
          </div>
          {/* L1-001/L4-005: install-app, notifications and the language toggle collapse in here
              below `sm` instead of overflowing the header row — nothing is dropped, it just moves
              one tap away. (Duplicated with the inline `sm:` versions above on tablet widths
              where both the drawer and the inline cluster can show; harmless, not the overflow
              this fixes.) */}
          <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3 sm:hidden" data-testid="mobile-extra-actions">
            <InstallAppButton />
            <NotificationBell />
            <div className="flex items-center rounded-full border border-border p-0.5" data-testid="mobile-language-toggle">
              {languageOptions.map((o) => (
                <button
                  key={o.code}
                  onClick={() => changeLanguage(o.code)}
                  data-testid={`mobile-language-option-${o.code}`}
                  className={`min-h-11 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-200 ${
                    lang === o.code ? "bg-[hsl(var(--primary))] text-primary-foreground" : "text-muted-foreground"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <EmergencyDialog open={sosOpen} onOpenChange={setSosOpen} />
    </header>
  );
}
